import React, { useState } from 'react';
import { Calendar, Clock, MapPin, FileText, Users, PartyPopper, Briefcase, Dumbbell, DollarSign, Search, Check, X, MessageCircle, HelpCircle, Plus, Trash2 } from 'lucide-react';

const EVENT_TYPES = [
  { id: 'social', label: 'Social Event', icon: PartyPopper, color: 'pink', description: 'Team dinners, celebrations, parties' },
  { id: 'meeting', label: 'Meeting', icon: Briefcase, color: 'blue', description: 'Team meetings, AGMs, planning sessions' },
  { id: 'training', label: 'Training', icon: Dumbbell, color: 'green', description: 'Practice sessions, fitness training' },
  { id: 'fundraiser', label: 'Fundraiser', icon: DollarSign, color: 'yellow', description: 'Fundraising events, charity matches' },
  { id: 'poll', label: 'Poll', icon: HelpCircle, color: 'indigo', description: 'Ask members a question with options' },
  { id: 'other', label: 'Other', icon: Calendar, color: 'purple', description: 'Any other club event' }
];

const AddEvent = ({ onBack, onSave, players = [], teams = [], initialValues = null }) => {
  const [eventName, setEventName] = useState(initialValues?.name || '');
  const [eventType, setEventType] = useState(initialValues?.eventType || 'social');
  const [eventDate, setEventDate] = useState('');
  const [eventTime, setEventTime] = useState('');
  const [eventEndTime, setEventEndTime] = useState('');
  const [eventVenue, setEventVenue] = useState(initialValues?.venue || '');
  const [eventDescription, setEventDescription] = useState(initialValues?.description || '');
  const [pollOptions, setPollOptions] = useState(['', '']); // Default to 2 empty options for polls
  const [whatsappGroupLink, setWhatsappGroupLink] = useState('');
  const [inviteType, setInviteType] = useState('all'); // 'all', 'teams', 'individuals'
  const [allowGuests, setAllowGuests] = useState(true);
  const [askMealPreference, setAskMealPreference] = useState(false);
  const [saving, setSaving] = useState(false);

  // Player selection state
  const [selectedPlayers, setSelectedPlayers] = useState([]);
  const [selectedTeams, setSelectedTeams] = useState([]); // Array of team IDs
  const [playerSearch, setPlayerSearch] = useState('');
  const [selectedTeamFilter, setSelectedTeamFilter] = useState('all');

  // Get unique players from all teams OR from players prop
  const getAllPlayers = () => {
    const playerMap = new Map();

    // First try to get players from teams
    teams.forEach(team => {
      (team.players || []).forEach(player => {
        const oderId = player.userId || player.id;
        if (oderId && !playerMap.has(oderId)) {
          playerMap.set(oderId, {
            ...player,
            oderId: oderId,
            teamName: team.name,
            teamId: team.id
          });
        }
      });
    });

    // If no players from teams, use the players prop as fallback
    if (playerMap.size === 0 && players.length > 0) {
      players.forEach(player => {
        const oderId = player.userId || player.id;
        if (oderId && !playerMap.has(oderId)) {
          playerMap.set(oderId, {
            ...player,
            oderId: oderId,
            teamName: player.teamName || 'Club Member',
            teamId: player.teamId || 'unknown'
          });
        }
      });
    }

    return Array.from(playerMap.values());
  };

  const allPlayers = getAllPlayers();

  // Debug log
  console.log('🔵 AddEvent - teams:', teams.length, 'players prop:', players.length, 'allPlayers:', allPlayers.length);

  // Filter players based on search and team filter
  const filteredPlayers = allPlayers.filter(player => {
    const matchesSearch = player.name?.toLowerCase().includes(playerSearch.toLowerCase()) ||
      player.email?.toLowerCase().includes(playerSearch.toLowerCase());
    const matchesTeam = selectedTeamFilter === 'all' || player.teamId === selectedTeamFilter;
    return matchesSearch && matchesTeam;
  });

  const toggleTeam = (teamId) => {
    setSelectedTeams(prev =>
      prev.includes(teamId)
        ? prev.filter(id => id !== teamId)
        : [...prev, teamId]
    );
  };

  const togglePlayer = (playerId) => {
    setSelectedPlayers(prev =>
      prev.includes(playerId)
        ? prev.filter(id => id !== playerId)
        : [...prev, playerId]
    );
  };

  const selectAllFiltered = () => {
    const filteredIds = filteredPlayers.map(p => p.oderId);
    setSelectedPlayers(prev => {
      const newSelection = [...prev];
      filteredIds.forEach(id => {
        if (!newSelection.includes(id)) {
          newSelection.push(id);
        }
      });
      return newSelection;
    });
  };

  const deselectAllFiltered = () => {
    const filteredIds = filteredPlayers.map(p => p.oderId);
    setSelectedPlayers(prev => prev.filter(id => !filteredIds.includes(id)));
  };

  const handleSave = async () => {
    if (!eventName.trim()) {
      alert('Please enter an event name');
      return;
    }
    if (!eventDate) {
      alert('Please select an event date');
      return;
    }

    // Validate invites based on type
    if (inviteType === 'teams' && selectedTeams.length === 0) {
      alert('Please select at least one team to invite');
      return;
    }
    if (inviteType === 'individuals' && selectedPlayers.length === 0) {
      alert('Please select at least one player to invite');
      return;
    }

    let finalPollOptions = null;
    if (eventType === 'poll') {
      finalPollOptions = pollOptions.filter(opt => opt.trim() !== '');
      if (finalPollOptions.length < 2) {
        alert('Please provide at least 2 options for the poll.');
        return;
      }
    }

    setSaving(true);
    try {
      // Calculate final invited players list
      let finalInvitedPlayers = [];
      let inviteAll = false;

      if (inviteType === 'all') {
        inviteAll = true;
        finalInvitedPlayers = []; // Empty means everyone
      } else if (inviteType === 'teams') {
        // Gather all players from selected teams
        const teamPlayerIds = new Set();
        selectedTeams.forEach(teamId => {
          const team = teams.find(t => t.id === teamId);
          if (team && team.players) {
            team.players.forEach(p => {
              const pid = p.userId || p.id;
              if (pid) teamPlayerIds.add(pid);
            });
          }
        });
        finalInvitedPlayers = Array.from(teamPlayerIds);

        if (finalInvitedPlayers.length === 0) {
          alert('Selected teams have no players. Please add players to teams first.');
          setSaving(false);
          return;
        }
      } else {
        // Individuals
        finalInvitedPlayers = selectedPlayers;
      }

      await onSave({
        name: eventName.trim(),
        eventType,
        date: eventDate,
        time: eventType === 'poll' ? null : (eventTime || null),
        endTime: eventType === 'poll' ? null : (eventEndTime || null),
        venue: eventType === 'poll' ? null : (eventVenue.trim() || null),
        description: eventDescription.trim() || null,
        pollOptions: finalPollOptions,
        whatsappGroupLink: whatsappGroupLink.trim() || null,
        inviteAllMembers: inviteAll,
        invitedPlayers: finalInvitedPlayers,
        allowGuests,
        askMealPreference,
        rsvps: {},
        createdAt: new Date().toISOString()
      });
    } catch (error) {
      console.error('Error saving event:', error);
      alert('Error creating event');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <button onClick={onBack} className="text-gray-600 mb-4 hover:text-gray-800">
        ← Back to Events
      </button>

      <div className="bg-white rounded-xl shadow-sm p-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-3 bg-purple-100 rounded-full">
            <PartyPopper className="text-purple-600" size={28} />
          </div>
          <div>
            <h2 className="text-2xl font-bold">Create Event</h2>
            <p className="text-gray-600">Invite club members to your event</p>
          </div>
        </div>

        <div className="space-y-6">
          {/* Event Type Selection */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-3">
              Event Type *
            </label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {EVENT_TYPES.map(type => {
                const IconComponent = type.icon;
                const isSelected = eventType === type.id;
                return (
                  <button
                    key={type.id}
                    onClick={() => setEventType(type.id)}
                    className={`p-4 rounded-lg border-2 transition-all text-left ${isSelected
                      ? type.color === 'pink' ? 'border-pink-500 bg-pink-50' :
                        type.color === 'blue' ? 'border-blue-500 bg-blue-50' :
                          type.color === 'green' ? 'border-green-500 bg-green-50' :
                            type.color === 'yellow' ? 'border-yellow-500 bg-yellow-50' :
                              'border-purple-500 bg-purple-50'
                      : 'border-gray-200 hover:border-gray-300'
                      }`}
                  >
                    <IconComponent
                      size={24}
                      className={
                        isSelected
                          ? type.color === 'pink' ? 'text-pink-600' :
                            type.color === 'blue' ? 'text-blue-600' :
                              type.color === 'green' ? 'text-green-600' :
                                type.color === 'yellow' ? 'text-yellow-600' :
                                  'text-purple-600'
                          : 'text-gray-400'
                      }
                    />
                    <p className={`font-semibold mt-2 ${isSelected ? 'text-gray-900' : 'text-gray-700'}`}>
                      {type.label}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">{type.description}</p>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Event Name */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Event Name *
            </label>
            <input
              type="text"
              value={eventName}
              onChange={(e) => setEventName(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              placeholder="e.g., Season End Party, Annual General Meeting"
            />
          </div>

          {/* Date and Time Row */}
          <div className={`grid grid-cols-1 ${eventType === 'poll' ? 'sm:grid-cols-1' : 'sm:grid-cols-3'} gap-4`}>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                <Calendar size={16} className="inline mr-1" />
                {eventType === 'poll' ? 'Poll Deadline *' : 'Date *'}
              </label>
              <input
                type="date"
                value={eventDate}
                onChange={(e) => setEventDate(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
            {eventType !== 'poll' && (
              <>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    <Clock size={16} className="inline mr-1" />
                    Start Time
                  </label>
                  <input
                    type="time"
                    value={eventTime}
                    onChange={(e) => setEventTime(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    <Clock size={16} className="inline mr-1" />
                    End Time
                  </label>
                  <input
                    type="time"
                    value={eventEndTime}
                    onChange={(e) => setEventEndTime(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
              </>
            )}
          </div>

          {/* Venue (Hidden for polls) */}
          {eventType !== 'poll' && (
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                <MapPin size={16} className="inline mr-1" />
                Venue / Location
              </label>
              <input
                type="text"
                value={eventVenue}
                onChange={(e) => setEventVenue(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                placeholder="e.g., The Crown Pub, 123 Main Street"
              />
            </div>
          )}

          {/* Poll Options UI */}
          {eventType === 'poll' && (
            <div className="bg-indigo-50/50 border border-indigo-100 rounded-xl p-5 mb-2">
              <label className="block text-sm font-bold text-indigo-900 mb-4 flex items-center gap-2">
                <HelpCircle size={18} className="text-indigo-600" />
                Poll Options
              </label>

              <div className="space-y-3">
                {pollOptions.map((option, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <div className="flex-1 relative">
                      <input
                        type="text"
                        value={option}
                        onChange={(e) => {
                          const newOptions = [...pollOptions];
                          newOptions[index] = e.target.value;
                          setPollOptions(newOptions);
                        }}
                        placeholder={`Option ${index + 1}`}
                        className="w-full pl-4 pr-10 py-3 border border-indigo-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white placeholder-indigo-300 text-indigo-900"
                      />
                      {pollOptions.length > 2 && (
                        <button
                          onClick={() => {
                            const newOptions = pollOptions.filter((_, i) => i !== index);
                            setPollOptions(newOptions);
                          }}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-indigo-300 hover:text-red-500 transition-colors p-1"
                        >
                          <Trash2 size={16} />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {pollOptions.length < 10 && (
                <button
                  onClick={() => setPollOptions([...pollOptions, ''])}
                  className="mt-4 flex items-center gap-2 text-sm font-semibold text-indigo-600 hover:text-indigo-800 transition-colors px-2 py-1"
                >
                  <Plus size={16} /> Add another option
                </button>
              )}
            </div>
          )}

          {/* Description */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              <FileText size={16} className="inline mr-1" />
              Description
            </label>
            <textarea
              value={eventDescription}
              onChange={(e) => setEventDescription(e.target.value)}
              rows={3}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
              placeholder="Tell members what this event is about..."
            />
          </div>

          {/* WhatsApp Group Link */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              <MessageCircle size={16} className="inline mr-1 text-green-600" />
              WhatsApp Group Link
            </label>
            <input
              type="url"
              value={whatsappGroupLink}
              onChange={(e) => setWhatsappGroupLink(e.target.value)}
              className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent ${whatsappGroupLink && !whatsappGroupLink.startsWith('https://chat.whatsapp.com/')
                ? 'border-red-300 bg-red-50'
                : 'border-gray-300'
                }`}
              placeholder="https://chat.whatsapp.com/..."
            />
            {whatsappGroupLink && !whatsappGroupLink.startsWith('https://chat.whatsapp.com/') && (
              <p className="text-xs text-red-600 mt-1">
                ⚠️ WhatsApp links should start with "https://chat.whatsapp.com/"
              </p>
            )}
            <p className="text-xs text-gray-500 mt-1">
              💡 Create a group for this event: WhatsApp → Group → Group info → Invite via link → Copy link
            </p>
          </div>

          {/* Invite Options */}
          <div className="bg-gray-50 rounded-lg p-4 space-y-4">
            <h4 className="font-semibold text-gray-700 flex items-center gap-2">
              <Users size={18} />
              Invite Options
            </h4>

            {/* Invite Type Selection */}
            <div className="flex flex-col gap-3">
              <label className="flex items-center gap-3 cursor-pointer p-3 border rounded-lg hover:bg-gray-50 transition-colors">
                <input
                  type="radio"
                  name="inviteType"
                  checked={inviteType === 'all'}
                  onChange={() => setInviteType('all')}
                  className="w-5 h-5 border-gray-300 text-purple-600 focus:ring-purple-500"
                />
                <div>
                  <span className="font-medium text-gray-700">Invite all club members</span>
                  <p className="text-sm text-gray-500">All {allPlayers.length} registered players</p>
                </div>
              </label>

              <label className="flex items-center gap-3 cursor-pointer p-3 border rounded-lg hover:bg-gray-50 transition-colors">
                <input
                  type="radio"
                  name="inviteType"
                  checked={inviteType === 'teams'}
                  onChange={() => setInviteType('teams')}
                  className="w-5 h-5 border-gray-300 text-purple-600 focus:ring-purple-500"
                />
                <div>
                  <span className="font-medium text-gray-700">Invite specific teams</span>
                  <p className="text-sm text-gray-500">Select one or more teams</p>
                </div>
              </label>

              <label className="flex items-center gap-3 cursor-pointer p-3 border rounded-lg hover:bg-gray-50 transition-colors">
                <input
                  type="radio"
                  name="inviteType"
                  checked={inviteType === 'individuals'}
                  onChange={() => setInviteType('individuals')}
                  className="w-5 h-5 border-gray-300 text-purple-600 focus:ring-purple-500"
                />
                <div>
                  <span className="font-medium text-gray-700">Select specific players</span>
                  <p className="text-sm text-gray-500">Pick individual members</p>
                </div>
              </label>
            </div>

            {/* Team Selection Panel */}
            {inviteType === 'teams' && (
              <div className="mt-4 border-t pt-4">
                <p className="text-sm font-medium text-gray-700 mb-3">
                  Select Teams ({selectedTeams.length} selected)
                </p>

                {teams.length === 0 ? (
                  <p className="text-sm text-gray-500 italic">No teams available.</p>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {teams.map(team => {
                      const isSelected = selectedTeams.includes(team.id);
                      return (
                        <div
                          key={team.id}
                          onClick={() => toggleTeam(team.id)}
                          className={`p-3 border rounded-lg cursor-pointer transition-all flex items-center justify-between ${isSelected
                            ? 'bg-purple-50 border-purple-300 ring-1 ring-purple-300'
                            : 'bg-white border-gray-200 hover:border-purple-200'
                            }`}
                        >
                          <div>
                            <p className={`font-medium ${isSelected ? 'text-purple-900' : 'text-gray-900'}`}>
                              {team.name}
                            </p>
                            <p className="text-xs text-gray-500">
                              {team.players ? team.players.length : 0} players
                            </p>
                          </div>
                          {isSelected && <Check size={18} className="text-purple-600" />}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Player Selection (shown when inviting individuals) */}
            {inviteType === 'individuals' && (
              <div className="mt-4 border-t pt-4">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-medium text-gray-700">
                    Selected: {selectedPlayers.length} player{selectedPlayers.length !== 1 ? 's' : ''}
                  </span>
                  <div className="flex gap-2">
                    <button
                      onClick={selectAllFiltered}
                      className="text-xs px-2 py-1 bg-purple-100 text-purple-700 rounded hover:bg-purple-200"
                    >
                      Select All
                    </button>
                    <button
                      onClick={deselectAllFiltered}
                      className="text-xs px-2 py-1 bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
                    >
                      Clear
                    </button>
                  </div>
                </div>

                {/* Search and Filter */}
                <div className="flex gap-2 mb-3">
                  <div className="relative flex-1">
                    <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      value={playerSearch}
                      onChange={(e) => setPlayerSearch(e.target.value)}
                      placeholder="Search players..."
                      className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>
                  {teams.length > 1 && (
                    <select
                      value={selectedTeamFilter}
                      onChange={(e) => setSelectedTeamFilter(e.target.value)}
                      className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    >
                      <option value="all">All Teams</option>
                      {teams.map(team => (
                        <option key={team.id} value={team.id}>{team.name}</option>
                      ))}
                    </select>
                  )}
                </div>

                {/* Player List */}
                <div className="max-h-60 overflow-y-auto border border-gray-200 rounded-lg bg-white">
                  {allPlayers.length === 0 ? (
                    <div className="p-4 text-center">
                      <p className="text-gray-500 text-sm">No players found in your teams.</p>
                      <p className="text-xs text-gray-400 mt-1">Make sure players are added to teams first.</p>
                    </div>
                  ) : filteredPlayers.length === 0 ? (
                    <p className="p-4 text-center text-gray-500 text-sm">No players match your search</p>
                  ) : (
                    filteredPlayers.map(player => {
                      const isSelected = selectedPlayers.includes(player.oderId);
                      return (
                        <div
                          key={player.oderId}
                          onClick={() => togglePlayer(player.oderId)}
                          className={`flex items-center justify-between px-3 py-2 border-b last:border-b-0 cursor-pointer transition-colors ${isSelected ? 'bg-purple-50' : 'hover:bg-gray-50'
                            }`}
                        >
                          <div className="flex items-center gap-3">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${isSelected ? 'bg-purple-600 text-white' : 'bg-gray-200 text-gray-600'
                              }`}>
                              {isSelected ? <Check size={16} /> : player.name?.charAt(0).toUpperCase()}
                            </div>
                            <div>
                              <p className="font-medium text-gray-900 text-sm">{player.name}</p>
                              <p className="text-xs text-gray-500">{player.teamName}</p>
                            </div>
                          </div>
                          {isSelected && (
                            <Check size={18} className="text-purple-600" />
                          )}
                        </div>
                      );
                    })
                  )}
                </div>

                {/* Selected Players Summary */}
                {selectedPlayers.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {selectedPlayers.slice(0, 5).map(playerId => {
                      const player = allPlayers.find(p => p.oderId === playerId);
                      return player ? (
                        <span
                          key={playerId}
                          className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-xs"
                        >
                          {player.name}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              togglePlayer(playerId);
                            }}
                            className="hover:text-purple-900"
                          >
                            <X size={12} />
                          </button>
                        </span>
                      ) : null;
                    })}
                    {selectedPlayers.length > 5 && (
                      <span className="text-xs text-gray-500 py-1">
                        +{selectedPlayers.length - 5} more
                      </span>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Allow Guests */}
            <label className="flex items-center gap-3 cursor-pointer pt-2 border-t">
              <input
                type="checkbox"
                checked={allowGuests}
                onChange={(e) => setAllowGuests(e.target.checked)}
                className="w-5 h-5 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
              />
              <div>
                <span className="font-medium text-gray-700">Allow guests</span>
                <p className="text-sm text-gray-500">Members can bring additional guests</p>
              </div>
            </label>

            {/* Ask Meal Preference */}
            <label className="flex items-center gap-3 cursor-pointer pt-2 border-t">
              <input
                type="checkbox"
                checked={askMealPreference}
                onChange={(e) => setAskMealPreference(e.target.checked)}
                className="w-5 h-5 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
              />
              <div>
                <span className="font-medium text-gray-700">Ask for meal preference</span>
                <p className="text-sm text-gray-500">Players can select Veg/Non-Veg when RSVPing</p>
              </div>
            </label>
          </div>

          {/* Save Button */}
          <button
            onClick={handleSave}
            disabled={saving || !eventName.trim() || !eventDate || (inviteType === 'individuals' && selectedPlayers.length === 0) || (inviteType === 'teams' && selectedTeams.length === 0)}
            className={`w-full py-4 rounded-lg font-semibold text-lg transition-all flex items-center justify-center gap-2 ${saving || !eventName.trim() || !eventDate || (inviteType === 'individuals' && selectedPlayers.length === 0) || (inviteType === 'teams' && selectedTeams.length === 0)
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
              : 'bg-purple-600 text-white hover:bg-purple-700'
              }`}
          >
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                Creating Event...
              </>
            ) : (
              <>
                <PartyPopper size={20} />
                Create Event & Send Invites
                {inviteType !== 'all' && (
                  <span className="ml-1">
                    {inviteType === 'teams' ? `(${selectedTeams.length} teams)` : `(${selectedPlayers.length})`}
                  </span>
                )}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddEvent;